import type { WaitSoundVolume } from "@/types/ai-assistant-style";

/**
 * Parameter murni bunyi tunggu "napas" mode suara (#60): satu tarikan lalu satu
 * hembusan dari derau yang disaring + kurva amplitudo naik-turun. Tidak menyentuh
 * Web Audio; pemutarnya ada di `voice-breath-player.ts`.
 */

/** Tarikan napas: 0–500 ms. */
export const INHALE_MS = 500;
/** Jeda singkat antara tarik dan hembus. */
export const BREATH_PAUSE_MS = 120;
/** Hembusan: lebih panjang dan meluruh pelan. */
export const EXHALE_MS = 680;
/** Panjang satu napas (±1,3 dtk). */
export const BREATH_MS = INHALE_MS + BREATH_PAUSE_MS + EXHALE_MS;
/** Puncak tarikan relatif terhadap hembusan: tarikan lebih lembut. */
const INHALE_PEAK = 0.55;
/** Makin kecil, puncak hembusan makin cepat dicapai lalu meluruh pelan. */
const EXHALE_SKEW = 0.7;
/** Jumlah titik kurva yang dikirim ke Web Audio. */
export const BREATH_CURVE_POINTS = 128;

/** Pusat filter derau: tarikan lebih terang, hembusan menurun lebih gelap. */
const FILTER_INHALE_HZ = { from: 1500, to: 2200 };
const FILTER_EXHALE_HZ = { from: 1300, to: 650 };
/** Lebar pita filter (Q rendah = lebar, terdengar seperti desis napas). */
export const BREATH_FILTER_Q = 0.7;
/** Potong frekuensi tinggi supaya tidak mendesis tajam. */
export const BREATH_LOWPASS_HZ = 3000;

/** Penguatan puncak per volume pilihan admin (0–1, sebelum filter). */
export const WAIT_SOUND_GAIN: Readonly<Record<WaitSoundVolume, number>> = {
	pelan: 0.1,
	sedang: 0.25,
	keras: 0.5,
};

const lerp = (a: number, b: number, x: number) => a + (b - a) * x;
const lobe = (x: number) => Math.sin(Math.PI * x) ** 2;

/** Amplitudo 0–1 pada `tMs` sejak napas mulai; 0 di luar rentang. */
export function breathEnvelope(tMs: number): number {
	if (tMs <= 0 || tMs >= BREATH_MS) return 0;
	if (tMs < INHALE_MS) return INHALE_PEAK * lobe(tMs / INHALE_MS);
	const exhaleStart = INHALE_MS + BREATH_PAUSE_MS;
	if (tMs <= exhaleStart) return 0;
	return lobe(((tMs - exhaleStart) / EXHALE_MS) ** EXHALE_SKEW);
}

/** Pusat filter (Hz) pada `tMs`: naik saat tarik, turun saat hembus. */
export function breathFilterHz(tMs: number): number {
	const t = Math.min(Math.max(tMs, 0), BREATH_MS);
	if (t < INHALE_MS)
		return lerp(FILTER_INHALE_HZ.from, FILTER_INHALE_HZ.to, t / INHALE_MS);
	const exhaleStart = INHALE_MS + BREATH_PAUSE_MS;
	if (t <= exhaleStart) return FILTER_INHALE_HZ.to;
	return lerp(
		FILTER_EXHALE_HZ.from,
		FILTER_EXHALE_HZ.to,
		(t - exhaleStart) / EXHALE_MS,
	);
}

/** Kurva seragam sepanjang satu napas untuk `setValueCurveAtTime`. */
export function breathCurve(
	fn: (tMs: number) => number,
	scale = 1,
	points = BREATH_CURVE_POINTS,
): Float32Array {
	const curve = new Float32Array(points);
	for (let i = 0; i < points; i++)
		curve[i] = fn((i / (points - 1)) * BREATH_MS) * scale;
	return curve;
}
